// Mounts the plugin system into the live app: builds it once (with toast UI, a
// localStorage grant store, and a workspace-scoped file reader) and exposes a
// "Plugins" sidebar panel. The example plugin is installed by createAppPluginSystem.

import { type ReactElement } from 'react';
import { Puzzle } from 'lucide-react';
import type { PlatformBridge } from '@slate/core';
import { SidebarPanelRegistry } from '../sidebar/SidebarPanelRegistry';
import { useDocStore } from '../store/docStore';
import { toast } from '../store/toastStore';
import { createAppPluginSystem } from './appPluginSystem';
import { PluginSurfaceModal } from './PluginSurfaceModal';
import { SurfaceHost } from './surfaces/SurfaceHost';
import { desktopSurfaceSrc } from './surfaces/surfaceSrc';
import type { PluginSystem } from './setup';
import { MarketplacePanel } from '../marketplace/MarketplacePanel';
import { createLocalPluginSource, CURATED_PLUGINS, type PluginSource } from '../marketplace/source';
import { createInstallFlow, type InstallFlow } from '../marketplace/installFlow';

let system: PluginSystem | null = null;
let source: PluginSource | null = null;
let flow: InstallFlow | null = null;

/** Build the app plugin system once. Scope: a plugin may read a file only if it
 *  is an open document or sits within an open workspace root. */
function ensureSystem(platform: PlatformBridge): PluginSystem {
  if (system) return system;
  system = createAppPluginSystem({
    platform,
    showMessage: (message) => toast(message, { kind: 'info' }),
    isPathAllowed: (path) => {
      const s = useDocStore.getState();
      if (s.docs.some((d) => d.ref?.path === path)) return true;
      return s.workspaceRoots.some((r) => path === r.path || path.startsWith(`${r.path}/`));
    },
    getActiveText: () => {
      const s = useDocStore.getState();
      return s.docs.find((d) => d.id === s.activeId)?.text;
    },
  });
  source = createLocalPluginSource(CURATED_PLUGINS);
  flow = createInstallFlow({ system, source });
  return system;
}

/** Register the Plugins sidebar panel (activity-bar icon + body). */
export function registerPluginsSidebarPanel(platform: PlatformBridge): () => void {
  const sys = ensureSystem(platform);
  return SidebarPanelRegistry.register({
    id: 'plugins',
    title: 'Plugins',
    icon: Puzzle,
    order: 50,
    // Sits at the bottom of the rail, below the file actions and a separator.
    placement: 'bottom',
    Component: () => (
      <>
        <MarketplacePanel system={sys} source={source!} flow={flow!} />
        <PluginSurfaceModal system={sys} />
      </>
    ),
  });
}

/** Public accessor for the one app plugin-system singleton (the same instance the
 *  sidebar Plugins panel uses). Idempotent: builds on first call, then caches. */
export function getAppPluginSystem(platform: PlatformBridge): PluginSystem {
  return ensureSystem(platform);
}

/** Render a plugin's `type:'panel'` surface as a sidebar body. */
export function renderPanelSurface(
  platform: PlatformBridge,
  pluginId: string,
  surfaceId: string,
): ReactElement | null {
  const sys = ensureSystem(platform);
  const surface = sys.host.listSurfaces().find((s) => s.pluginId === pluginId && s.id === surfaceId);
  if (!surface) return null;
  return (
    <SurfaceHost
      system={sys}
      pluginId={pluginId}
      surfaceId={surfaceId}
      src={desktopSurfaceSrc(pluginId, surface.path)}
      title={surfaceId}
    />
  );
}
